import {
  ConnectionState,
  DeviceUnsupportedError,
  LocalAudioTrack,
  LocalParticipant,
  LocalVideoTrack,
  LogLevel,
  ParticipantEvent,
  Room,
  RoomEvent,
  setLogLevel,
  Track,
} from 'livekit-client'
import type { LocalTrackPublication, Participant, RoomOptions } from 'livekit-client'

// LiveKit's own SDK logs infrastructure details at info/debug level (server edition/version/
// protocol, region, node id — e.g. "connected to Livekit Server edition: 0, version: 1.13.3,
// ..., nodeId: ND_..., region: ..."). None of that belongs in a user-facing console, so keep
// LiveKit's internal loggers at `warn` — real connection failures still surface, but server
// topology never does.
setLogLevel(LogLevel.warn)
import { markRaw, reactive } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { AsyncQueue } from './queue'
import { CallSession } from './session'
import { cameraPreset } from './profiles'
import {
  disableScreenShare,
  effectiveScreenProfile,
  enableBuiltinScreenShare,
  publishCustomScreenShare,
  reconfigureScreenShareTrack,
} from './screenShareController'
import { enumerateDevices, switchCameraDevice, switchMicrophoneDevice, switchSpeakerDevice } from './deviceController'
import { setCallAudioSessionActive } from './audioSession'
import { hasLabels, loadDeviceListCache, saveDeviceListCache, type CachedDeviceInfo } from './deviceListCache'
import {
  type DevicePreferenceSlot,
  clearDevicePreference,
  getDevicePreference,
  resolveDevicePreference,
  saveDevicePreference,
  selectableDevices,
} from './devicePreferences'
import { MicAudioController } from './micAudioController'
import { isNativeMicAvailable, listNativeMicrophones, listNativeSpeakers, NativeMicSource } from './nativeMic'
import { listen, type UnlistenFn } from '@tauri-apps/api/event'
import type { LiveKitState, ScreenShareQuality, VideoQuality } from './types'
import {
  clampChannelAudioBitrateKbps,
  DEFAULT_CHANNEL_AUDIO_BITRATE_KBPS,
  getParticipantVolume,
  loadAppSettings,
  MAX_MIC_SENSITIVITY_THRESHOLD,
  MIN_MIC_SENSITIVITY_THRESHOLD,
  patchAppSettings,
  setParticipantVolume as persistParticipantVolume,
} from '~/lib/settings/appSettings'
import { soundManager, SoundType } from '~/lib/soundManager'

type MediaDeviceKey = 'selectedMicId' | 'selectedCameraId' | 'selectedSpeakerId'

const MEDIA_KEY_SLOT: Record<MediaDeviceKey, DevicePreferenceSlot> = {
  selectedMicId: 'microphone',
  selectedCameraId: 'camera',
  selectedSpeakerId: 'speaker',
}

/**
 * `LocalTrackPublication.pauseUpstream()` throws `DeviceUnsupportedError` on anything LiveKit's
 * `getBrowser()` sniffing classifies as "Safari < 12" (working around a real old-WebKit crash,
 * https://bugs.webkit.org/show_bug.cgi?id=184911) — Tauri's WKWebView user agent apparently
 * parses that way often enough to trip it in practice, even though the actual engine underneath
 * is current and unaffected by that bug. Worse, LiveKit's own implementation flips its internal
 * `_isUpstreamPaused` flag and emits `TrackEvent.UpstreamPaused` *before* the version check, so a
 * caller that just logs and swallows this error ends up believing upstream is paused while the
 * `RTCRtpSender` was never actually repointed — the mic keeps transmitting. This replicates the
 * one line `pauseUpstream()` never got to run (`sender.replaceTrack(null)`) as a fallback.
 */
async function pauseUpstreamCompat(publication: LocalTrackPublication, track: LocalAudioTrack) {
  try {
    await publication.pauseUpstream()
  } catch (error) {
    if (!(error instanceof DeviceUnsupportedError)) throw error
    if (track.sender && track.sender.transport?.state !== 'closed') {
      await track.sender.replaceTrack(null)
    }
  }
}

/**
 * Mic audio encoding preset for a channel's `Channel.bitrate` (kbps, server-persisted and
 * validated to 8-128) — falls back to a sane default for channels with no bitrate set yet
 * (unset channels, or channels created before this field existed).
 */
function audioPresetForChannel(bitrateKbps: number | undefined) {
  return { maxBitrate: clampChannelAudioBitrateKbps(bitrateKbps ?? DEFAULT_CHANNEL_AUDIO_BITRATE_KBPS) * 1000 }
}

/**
 * Public facade for the whole call subsystem. Owns the reactive read-model
 * every store/component consumes, the media-mutation queue that serializes
 * all of the methods below, and the single active `CallSession`. Every async
 * method re-checks `assertCurrent(session)` after each `await` so a
 * superseded session (channel switch, disconnect) can never mutate state on
 * behalf of a call that's no longer current.
 *
 * See VOICE_CALLS_ARCHITECTURE.md for the full invariant list this class
 * must uphold.
 */
export // A participant's screen share: its video and its audio, subscribed together on "Смотреть стрим".
const SCREEN_SHARE_SOURCES: Track.Source[] = [Track.Source.ScreenShare, Track.Source.ScreenShareAudio]

class LiveKitManager {
  private readonly queue = new AsyncQueue()
  private readonly persistedSettings = loadAppSettings()
  private readonly micAudio = new MicAudioController()
  // macOS desktop: the mic comes from Rust capture, never `getUserMedia` — see `nativeMic.ts`.
  private readonly useNativeMic = isNativeMicAvailable()
  private session: CallSession | null = null
  private generation = 0
  // True once `refreshDevices()` has actually gotten a real, labeled mic/camera device list back
  // this session — see `ensureDeviceLabelsUnlocked`. Kept as two independent flags: evidence the
  // mic was unlocked says nothing about the camera, and vice versa — conflating them is what
  // previously caused the camera to silently open (lighting its hardware indicator) at startup
  // just because the mic had a prior grant. Never reset back to false once set: per
  // `deviceChangeHandler`'s comment, LiveKit's own `DeviceManager` keeps surfacing real labels
  // through plain `enumerateDevices()` for the rest of the page's lifetime after the first unlock.
  private sessionMicLabelsConfirmed = false
  private sessionCameraLabelsConfirmed = false
  // `navigator.mediaDevices.devicechange` fires for every USB camera/mic, Bluetooth headset, or
  // audio-output change — always re-enumerate label-lessly here. Never `requestPermissions: true`
  // from an automatic OS event: that would silently re-open the mic/camera every time a device is
  // plugged in, which is exactly the "quiet" activation this system exists to prevent. Once
  // permission has actually been granted (either the startup label refresh in `initializeDevices`
  // when it was already granted in a previous session, or an explicit click this session — the
  // settings page's "Обновить" button), the browser keeps surfacing real labels through plain
  // `enumerateDevices()` for the rest of the page's lifetime — that's the whole point of LiveKit's
  // own `isDummyDeviceOrEmpty` check in `DeviceManager.getDevices` only escalating to
  // `getUserMedia` when labels are still blank — so a hotplug never needs another permission grab
  // to pick up a new device's name; it only ever needed one, per session.
  private readonly deviceChangeHandler = () => { void this.refreshDevices(false) }
  // macOS: WebKit's `devicechange` never fires without a `getUserMedia` grant — Rust watches
  // CoreAudio instead (`native_speaker::watch_devices`).
  private unlistenNativeDevices: UnlistenFn | null = null

  readonly state: LiveKitState = reactive({
    room: null,
    roomState: ConnectionState.Disconnected,
    isReconnecting: false,
    participants: [],
    // A standing preference, not a per-call default — persisted so it survives a restart and stays
    // identical across every channel join/switch instead of resetting to "unmuted" each time (see
    // `connect()`/`toggleMic()`).
    isMicMuted: this.persistedSettings.audio.micMuted,
    isCameraEnabled: false,
    isScreenShareEnabled: false,
    // Same standing-preference treatment as `isMicMuted` above.
    isDeafened: this.persistedSettings.audio.deafened,
    isNoiseSuppressionEnabled: this.persistedSettings.audio.noiseSuppressionEnabled,
    isNoiseSuppressionPending: false,
    isEchoCancellationEnabled: this.persistedSettings.audio.echoCancellationEnabled,
    // Purely transient diagnostic state — deliberately never persisted, and always off at the
    // start of a session/call (see `resetCallState`).
    isSelfMonitoring: false,
    activeVoiceChannelId: null,
    selectedMicId: this.persistedSettings.media.selectedMicId,
    selectedCameraId: this.persistedSettings.media.selectedCameraId,
    selectedSpeakerId: this.persistedSettings.media.selectedSpeakerId,
    microphones: [],
    cameras: [],
    speakers: [],
    micVolume: this.persistedSettings.audio.micVolume,
    speakerVolume: this.persistedSettings.audio.speakerVolume,
    micSensitivity: this.persistedSettings.audio.micSensitivity,
    videoQuality: this.persistedSettings.video.videoQuality,
    screenShareQuality: this.persistedSettings.video.screenShareQuality,
    screenShareFps: 30,
    isScreenShareAudioEnabled: this.persistedSettings.video.screenShareAudioEnabled,
    error: null,
    mutedParticipants: Object.fromEntries(
      Object.entries(this.persistedSettings.participantVolumes)
        .filter(([, volume]) => volume.muted)
        .map(([identity]) => [identity, true]),
    ),
    micPermission: 'prompt',
    cameraPermission: 'prompt',
    watchedScreenShares: {},
  })

  private micPermissionStatus: PermissionStatus | null = null
  private cameraPermissionStatus: PermissionStatus | null = null

  // "Прослушать себя" (`setSelfMonitoring`) — a Web Audio tap on our own outgoing track routed
  // straight into the shared `AudioContext`'s destination, plus the mic/deafen state saved so it
  // can be restored exactly once monitoring stops (see `setSelfMonitoring` for the full flow).
  private selfMonitorSourceNode: MediaStreamAudioSourceNode | null = null
  private selfMonitorRafId: number | null = null
  private selfMonitorTrackId: string | null = null
  private selfMonitorPrevMicMuted: boolean | null = null
  private selfMonitorPrevDeafened: boolean | null = null

  constructor() {
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler)
      void this.initializeDevices()
    }
    if (this.useNativeMic) {
      void listen('native-audio-devices-changed', () => {
        void this.refreshDevices(false)
        // «Как в системе» follows the system default mid-call: native playback is bound to the
        // device it opened, so reopen it (this also fires for other list changes — a short gap).
        if (this.session && this.state.selectedSpeakerId === 'default') void this.queue.run(() => this.startNativeSpeaker())
      }).then((unlisten) => { this.unlistenNativeDevices = unlisten })
    }
    this.installEarlyAudioContextPrime()
  }

  /**
   * Creating the shared `AudioContext` (`MicAudioController.prime()`) is what actually opens the
   * OS audio engine for the first time this run — on shared-mode backends (WASAPI on Windows,
   * similar on macOS/Linux) that first open commonly causes a brief, audible glitch in whatever
   * else is already playing through the same output (e.g. music), because the engine renegotiates
   * its shared format. It used to only happen inside `connect()`, i.e. at the exact moment the
   * user joins their first voice channel — the glitch landed right on top of the join, which reads
   * as the app doing something wrong at exactly the worst moment. `AudioContext` can only be
   * created/resumed inside a user gesture (autoplay policy), so instead of connect()'s click
   * specifically, this primes it on *any* first interaction anywhere in the app (a click, a
   * keypress) — happens once, early, and unrelated to any specific action, so the one-time glitch
   * (if the platform has one at all) is no longer tied to the join itself.
   */
  private installEarlyAudioContextPrime() {
    if (typeof window === 'undefined') return
    const prime = () => {
      window.removeEventListener('pointerdown', prime)
      window.removeEventListener('keydown', prime)
      this.micAudio.prime().catch(() => undefined)
    }
    window.addEventListener('pointerdown', prime, { once: true })
    window.addEventListener('keydown', prime, { once: true })
  }

  /**
   * Startup device enumeration. Checks the durable OS/browser permission grant first
   * (`refreshMediaPermissions` — never prompts, just reads `Permissions.query`), then always does a
   * label-less `refreshDevices(false)` — see `ensureDeviceLabelsUnlocked`'s doc comment for why this
   * never requests permissions here even when a prior grant is known: this app's WebView does not
   * reliably return real labels from a bare `enumerateDevices()` after a fresh reload, so requesting
   * here would fall through to `getUserMedia` and re-prompt on every reload. A previously-picked
   * device is matched back by `deviceId` where possible and re-applied once real labels become
   * available from an actual user gesture later in the session (see `restoreDevicePreferences`).
   */
  private async initializeDevices() {
    await this.refreshMediaPermissions()
    await this.ensureDeviceLabelsUnlocked()
    if (!this.sessionMicLabelsConfirmed || !this.sessionCameraLabelsConfirmed) await this.refreshDevices(false)
  }

  /**
   * Best-effort, non-blocking read of the OS/browser mic+camera grant state via the
   * `Permissions` API, kept live via `onchange` so a grant/revoke made in System Settings while
   * the app is open updates `state.micPermission`/`cameraPermission` without needing another
   * call/join. Never itself prompts for permission — that only happens where `getUserMedia` is
   * actually invoked (`setMicrophoneEnabled`/`setCameraEnabled`).
   */
  async refreshMediaPermissions() {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return
    const [mic, camera] = await Promise.all([
      this.queryPermission('microphone'),
      this.queryPermission('camera'),
    ])
    if (mic) {
      this.micPermissionStatus = mic
      this.state.micPermission = mic.state as any
      mic.onchange = () => { this.state.micPermission = mic.state as any }
    }
    if (camera) {
      this.cameraPermissionStatus = camera
      this.state.cameraPermission = camera.state as any
      camera.onchange = () => { this.state.cameraPermission = camera.state as any }
    }
  }

  private async queryPermission(name: 'microphone' | 'camera') {
    try {
      return await navigator.permissions.query({ name: name as PermissionName })
    } catch {
      // WebKit doesn't support querying every permission name on every version — leave the
      // corresponding state at its current value (defaults to 'prompt') rather than guessing.
      return null
    }
  }

  /** True once `getUserMedia` itself has told us a device is actually unreachable, regardless
   * of what the `Permissions` API (which WebKit only partially supports) last reported. */
  private markPermissionDenied(kind: 'mic' | 'camera', error: unknown) {
    const name = (error as any)?.name
    if (name !== 'NotAllowedError' && name !== 'PermissionDeniedError' && name !== 'SecurityError') return
    if (kind === 'mic') this.state.micPermission = 'denied'
    else this.state.cameraPermission = 'denied'
  }

  get room() {
    return this.state.room
  }

  async connect(channelId: string, serverUrl: string, token: string, options?: { canSpeak?: boolean; audioBitrateKbps?: number }) {
    return this.queue.run(async () => {
      if (this.session && !this.session.closed && this.session.channelId === channelId) return
      await this.disconnectInternal(false)

      await soundManager.play(SoundType.CONNECT)

      // Before any call audio starts, so WebKit sets up its audio session for a call from the
      // outset — see audioSession.ts. Undone in `resetCallState()`, which every disconnect path
      // (including a failed connect below) goes through.
      setCallAudioSessionActive(true)

      // Resolved before constructing `Room` so `createRoomOptions` can hand it our already-warmed
      // context (see the `webAudioMix` comment there) instead of `Room` opening its own.
      const audioContext = await this.micAudio.sharedAudioContext()
      if (this.useNativeMic) await this.startNativeSpeaker()
      const room = new Room(this.createRoomOptions(audioContext, options?.audioBitrateKbps))
      const session = new CallSession(room, channelId, ++this.generation, options?.audioBitrateKbps ?? DEFAULT_CHANNEL_AUDIO_BITRATE_KBPS)
      this.session = session
      this.state.room = markRaw(room)
      this.state.activeVoiceChannelId = channelId
      this.state.roomState = ConnectionState.Connecting
      this.state.error = null
      this.installRoomListeners(session)
      void this.refreshMediaPermissions()

      try {
        await room.connect(serverUrl, token)
        this.assertCurrent(session)
        this.state.roomState = room.state
        this.attachSpeakingListener(session, room.localParticipant)
        // `RoomEvent.ParticipantConnected` only fires for participants who join *after* us, so
        // anyone already in the room at connect time needs the fast per-participant listener
        // attached explicitly here too — otherwise their speaking indicator only updates on the
        // slower, polled `RoomEvent.ActiveSpeakersChanged` (see `installRoomListeners`).
        for (const participant of room.remoteParticipants.values()) {
          this.attachSpeakingListener(session, participant)
        }

        // Applies the standing deafen preference to this brand-new session's remote audio too —
        // `RoomEvent.TrackSubscribed` (see `installRoomListeners`) covers tracks that subscribe
        // after this point, but this covers anything already subscribed by the time we get here.
        this.micAudio.applyDeafState(session, this.state.isDeafened)

        // Respects the standing mute/deafen preference instead of always joining unmuted — see
        // `state.isMicMuted`/`state.isDeafened`. Deafening implies muted, same as `toggleDeaf()`.
        // `canSpeak: false` (no group Speak permission) overrides the preference entirely — the
        // mic must come up muted on every connect regardless of what was persisted.
        const canSpeak = options?.canSpeak !== false
        await this.setMicrophoneEnabled(session, canSpeak && !this.state.isMicMuted && !this.state.isDeafened)
        this.assertCurrent(session)
        this.state.isMicMuted = !room.localParticipant.isMicrophoneEnabled
        this.state.isCameraEnabled = room.localParticipant.isCameraEnabled
        this.state.isScreenShareEnabled = room.localParticipant.isScreenShareEnabled
        await this.syncNoiseSuppression(session)
        await this.syncMicVolume(session)
        this.updateParticipants(session)
      } catch (error: any) {
        this.markPermissionDenied('mic', error)
        this.state.error = error?.message ?? String(error)
        Sentry.captureException(error, { extra: { channelId, action: 'connect' } })
        await this.disconnectSession(session, false)
        throw error
      }
    })
  }

  async disconnect() {
    return this.queue.run(() => this.disconnectInternal(true))
  }

  async dispose() {
    await this.disconnect()
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler)
    }
    this.unlistenNativeDevices?.()
    this.unlistenNativeDevices = null
    await this.micAudio.close()
  }

  async toggleMic(deviceId?: string) {
    return this.queue.run(async () => {
      if (deviceId) this.saveMediaDevice('selectedMicId', deviceId)
      const session = this.session
      if (!session) {
        // Not in a call — nothing to enable/disable on a track yet, but the preference itself
        // still flips and persists so the next `connect()` (any channel) honors it.
        this.state.isMicMuted = !this.state.isMicMuted
        patchAppSettings({ audio: { micMuted: this.state.isMicMuted } })
        void soundManager.play(this.state.isMicMuted ? SoundType.MUTED : SoundType.NON_MUTED)
        return
      }
      const enable = this.state.isMicMuted
      try {
        await this.setMicrophoneEnabled(session, enable)
      } catch (error) {
        this.markPermissionDenied('mic', error)
        throw error
      }
      this.assertCurrent(session)
      this.state.isMicMuted = !session.room.localParticipant.isMicrophoneEnabled
      patchAppSettings({ audio: { micMuted: this.state.isMicMuted } })
      if (enable) {
        await this.syncNoiseSuppression(session)
        await this.syncMicVolume(session)
      } else {
        session.disposeMicGainChain()
      }
      void soundManager.play(this.state.isMicMuted ? SoundType.MUTED : SoundType.NON_MUTED)
      this.updateParticipants(session)
    })
  }

  async toggleCamera(deviceId?: string, forceState?: boolean) {
    return this.queue.run(async () => {
      if (deviceId) this.saveMediaDevice('selectedCameraId', deviceId)
      const session = this.session
      if (!session) return
      const enable = forceState ?? !this.state.isCameraEnabled
      try {
        await session.room.localParticipant.setCameraEnabled(enable, enable ? {
          deviceId: this.state.selectedCameraId,
          resolution: cameraPreset(this.state.videoQuality).resolution,
        } : undefined)
      } catch (error) {
        this.markPermissionDenied('camera', error)
        throw error
      }
      this.assertCurrent(session)
      this.state.isCameraEnabled = session.room.localParticipant.isCameraEnabled
      this.updateParticipants(session)
    })
  }

  async toggleScreenShare(forceState?: boolean) {
    return this.queue.run(async () => {
      const session = this.session
      if (!session) return
      const enable = forceState ?? !this.state.isScreenShareEnabled
      if (!enable) {
        await this.stopScreenShare(session)
        return
      }

      const profile = effectiveScreenProfile(this.state.screenShareQuality, this.state.screenShareFps)
      await enableBuiltinScreenShare(session, profile)
      this.assertCurrent(session)
      this.state.isScreenShareEnabled = session.room.localParticipant.isScreenShareEnabled
      this.updateParticipants(session)
    })
  }

  async startScreenShare(stream: MediaStream, quality: ScreenShareQuality, fps: number, includeAudio = true) {
    return this.queue.run(async () => {
      const session = this.session
      if (!session) {
        stream.getTracks().forEach(track => track.stop())
        throw new Error('Нет активного голосового канала')
      }
      await this.stopScreenShare(session)
      this.assertCurrent(session)

      this.state.screenShareQuality = quality
      this.state.screenShareFps = fps
      this.state.isScreenShareAudioEnabled = includeAudio
      patchAppSettings({ video: { screenShareQuality: quality, screenShareAudioEnabled: includeAudio } })

      const profile = effectiveScreenProfile(quality, fps)
      this.state.screenShareFps = profile.maxFramerate

      const localTracks = await publishCustomScreenShare(session, stream, profile, includeAudio, () => this.assertCurrent(session))
      session.adoptScreenShare(stream, localTracks, () => {
        void this.queue.run(async () => {
          if (this.session === session && !session.closed) await this.stopScreenShare(session)
        })
      })
      this.state.isScreenShareEnabled = true
      this.updateParticipants(session)
    })
  }

  async toggleDeaf() {
    return this.queue.run(async () => {
      const session = this.session
      if (!session) {
        // Not in a call — same as `toggleMic()`: just flip and persist the standing preference so
        // the next `connect()` (any channel) honors it.
        this.state.isDeafened = !this.state.isDeafened
        patchAppSettings({ audio: { deafened: this.state.isDeafened } })
        if (this.state.isDeafened) {
          this.state.isMicMuted = true
          patchAppSettings({ audio: { micMuted: true } })
        }
        void soundManager.play(this.state.isDeafened ? SoundType.DEAFENED : SoundType.NON_DEAFENED)
        return
      }
      this.state.isDeafened = !this.state.isDeafened
      patchAppSettings({ audio: { deafened: this.state.isDeafened } })
      this.micAudio.applyDeafState(session, this.state.isDeafened)
      await this.setMicrophoneEnabled(session, !this.state.isDeafened)
      this.assertCurrent(session)
      this.state.isMicMuted = !session.room.localParticipant.isMicrophoneEnabled
      // Deafen/undeafen also flips the mic as a side effect — keep the persisted preference in
      // sync with it too, same as `toggleMic()`, so a restart afterwards doesn't restore a stale value.
      patchAppSettings({ audio: { micMuted: this.state.isMicMuted } })
      if (!this.state.isMicMuted) {
        await this.syncNoiseSuppression(session)
        await this.syncMicVolume(session)
      }
      void soundManager.play(this.state.isDeafened ? SoundType.DEAFENED : SoundType.NON_DEAFENED)
      this.updateParticipants(session)
    })
  }

  async changeMicrophone(deviceId: string) {
    return this.queue.run(async () => {
      this.saveMediaDevice('selectedMicId', deviceId)
      const session = this.session
      if (!session) return
      if (this.useNativeMic) {
        // Same track before and after (only the Rust capture behind it changes), so the gain
        // chain/RNNoise stay valid. Not published yet → the next publish uses the new id.
        await session.getNativeMic()?.switchDevice(deviceId)
        return
      }
      session.disposeMicGainChain()
      await switchMicrophoneDevice(session, deviceId, this.state.isMicMuted, this.micAudio.microphoneCaptureOptions(this.state))
      this.assertCurrent(session)
      if (!this.state.isMicMuted) {
        await this.syncNoiseSuppression(session)
        await this.syncMicVolume(session)
      }
    })
  }

  async changeCamera(deviceId: string) {
    return this.queue.run(async () => {
      this.saveMediaDevice('selectedCameraId', deviceId)
      const session = this.session
      if (!session) return
      await switchCameraDevice(session, deviceId, this.state.isCameraEnabled, cameraPreset(this.state.videoQuality).resolution)
    })
  }

  async changeSpeaker(deviceId: string) {
    return this.queue.run(async () => {
      this.saveMediaDevice('selectedSpeakerId', deviceId)
      const session = this.session
      // macOS: playback is native (`nativeSpeaker.ts`), and these are CoreAudio names.
      if (this.useNativeMic) {
        if (session) await this.startNativeSpeaker()
        return
      }
      // Self-monitor playback rides the same shared `AudioContext` `Room` uses for every remote
      // participant (see `startSelfMonitorPlayback`), so `switchSpeakerDevice`'s own
      // `setSinkId` call already re-routes it too — no separate handling needed here.
      if (session) await switchSpeakerDevice(session, deviceId)
    })
  }

  async setMicVolume(volume: number) {
    this.state.micVolume = this.micAudio.normalizeVolume(volume)
    patchAppSettings({ audio: { micVolume: this.state.micVolume } })
    const session = this.session
    if (session) await this.syncMicVolume(session)
  }

  setMicSensitivity(threshold: number) {
    const clamped = Math.min(MAX_MIC_SENSITIVITY_THRESHOLD, Math.max(MIN_MIC_SENSITIVITY_THRESHOLD, threshold))
    this.state.micSensitivity = clamped
    patchAppSettings({ audio: { micSensitivity: clamped } })
  }

  setSpeakerVolume(volume: number) {
    const normalized = this.micAudio.normalizeVolume(volume)
    this.state.speakerVolume = normalized
    patchAppSettings({ audio: { speakerVolume: normalized } })
    const session = this.session
    if (!session) return
    for (const participant of session.room.remoteParticipants.values()) {
      for (const publication of participant.audioTrackPublications.values()) {
        const source = publication.track?.source
        if (!source) continue
        ;(publication.track as any)?.setVolume?.(this.effectiveParticipantVolume(participant.identity, source))
      }
    }
  }

  /** Combines the master "speaker" volume with a participant's per-track relative volume (0-200%, persisted). */
  private effectiveParticipantVolume(identity: string | undefined, source: Track.Source) {
    if (!identity) return this.state.speakerVolume
    const stored = getParticipantVolume(identity)
    const percent = source === Track.Source.ScreenShareAudio ? stored.screenShare : stored.mic
    return this.state.speakerVolume * (percent / 100)
  }

  /** Persists and, if connected, live-applies a participant's relative mic/screen-share volume (0-200%). */
  setParticipantVolume(identity: string, kind: 'mic' | 'screenShare', percent: number) {
    const clamped = Math.min(200, Math.max(0, percent))
    persistParticipantVolume(identity, kind === 'mic' ? { mic: clamped } : { screenShare: clamped })
    const session = this.session
    if (!session) return
    const participant = session.room.remoteParticipants.get(identity)
    if (!participant) return
    const source = kind === 'mic' ? Track.Source.Microphone : Track.Source.ScreenShareAudio
    participant.setVolume(this.effectiveParticipantVolume(identity, source), source)
  }

  /**
   * "Mute for me": persists locally (survives restarts) and, when connected, actually
   * unsubscribes from the participant's mic track via `setSubscribed(false)` rather than just
   * silencing playback — so muting also stops receiving their audio media entirely, and a fresh
   * subscription on their next reconnect gets caught by the `TrackSubscribed` check below before
   * we ever attach/hear it.
   */
  setParticipantMuted(identity: string, muted: boolean) {
    persistParticipantVolume(identity, { muted })
    this.state.mutedParticipants[identity] = muted
    const session = this.session
    if (!session) return
    const participant = session.room.remoteParticipants.get(identity)
    const publication = participant?.getTrackPublication(Track.Source.Microphone)
    publication?.setSubscribed(!muted)
  }

  /**
   * "Смотреть стрим": subscribes to (or, when `watched` is false, drops) a participant's screen share
   * video and audio. Until then those tracks are left unsubscribed — see the `TrackPublished` /
   * `TrackSubscribed` listeners — so nobody downloads a stream they didn't ask to watch.
   */
  setScreenShareWatched(identity: string, watched: boolean) {
    if (watched) this.state.watchedScreenShares[identity] = true
    else delete this.state.watchedScreenShares[identity]
    const participant = this.session?.room.remoteParticipants.get(identity)
    for (const source of SCREEN_SHARE_SOURCES) participant?.getTrackPublication(source)?.setSubscribed(watched)
  }

  private isUnwatchedScreenShare(publication: any, participant: any) {
    return SCREEN_SHARE_SOURCES.includes(publication?.source)
      && !!participant?.identity
      && !this.state.watchedScreenShares[participant.identity]
  }

  async setNoiseSuppressionEnabled(enabled: boolean) {
    return this.queue.run(async () => {
      this.state.isNoiseSuppressionEnabled = enabled
      patchAppSettings({ audio: { noiseSuppressionEnabled: enabled } })
      const session = this.session
      if (session) await this.syncNoiseSuppression(session)
    })
  }

  async setEchoCancellationEnabled(enabled: boolean) {
    return this.queue.run(async () => {
      this.state.isEchoCancellationEnabled = enabled
      patchAppSettings({ audio: { echoCancellationEnabled: enabled } })
      await this.restartMicrophoneIfActive()
    })
  }

  /**
   * "Прослушать себя" — plays our own outgoing mic audio (post gain-chain/RNNoise, i.e. exactly
   * what other participants currently hear) back through the selected output device, for sound
   * checking. While active we must not actually be heard, so this uses `pauseUpstream()` rather
   * than `setMicrophoneEnabled(false)`/`mute()` — both of those would also disable the
   * `MediaStreamTrack` we need to keep tapping locally (`mute()` flips `track.enabled = false`,
   * which zeroes the samples for every consumer, not just the RTP sender). `pauseUpstream()`
   * only swaps the sender's track to `null`, leaving capture/processing untouched. Also silences
   * incoming remote audio for the duration (`applyDeafState`) so the sound check isn't drowned
   * out by other participants talking, and drives `isMicMuted`/`isDeafened` to `true` purely
   * cosmetically so the rest of the UI (mute button, roster icons) agrees with what's actually
   * happening — neither is persisted, and both are restored to their pre-monitor values on stop.
   */
  async setSelfMonitoring(enabled: boolean) {
    return this.queue.run(async () => {
      if (enabled === this.state.isSelfMonitoring) return
      const session = this.session
      if (!session) return

      if (enabled) {
        const publication = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)
        const track = publication?.track
        if (!publication || !(track instanceof LocalAudioTrack)) {
          throw new Error('Микрофон сейчас не опубликован — нечего прослушивать')
        }
        // Resolved up front (and awaited) so `startSelfMonitorPlayback` itself can stay fully
        // synchronous — it flips `state.isSelfMonitoring` first and starts polling immediately
        // after, with no `await` in between where a stale read of that flag could make the very
        // first tick bail out before ever creating the playback graph.
        const audioContext = await this.micAudio.sharedAudioContext()
        this.assertCurrent(session)
        if (!audioContext) throw new Error('Не удалось получить аудиоконтекст для прослушивания')
        try {
          await pauseUpstreamCompat(publication, track)
        } catch (error) {
          console.error('[LiveKitManager] Failed to pause upstream for self-monitor', error)
          Sentry.captureException(error, { extra: { action: 'self_monitor_pause_upstream' } })
          return
        }
        this.assertCurrent(session)
        this.selfMonitorPrevMicMuted = this.state.isMicMuted
        this.selfMonitorPrevDeafened = this.state.isDeafened
        this.state.isMicMuted = true
        this.state.isDeafened = true
        this.micAudio.applyDeafState(session, true)
        this.state.isSelfMonitoring = true
        this.startSelfMonitorPlayback(session, audioContext)
        // Same cue `toggleDeaf()` uses for `isDeafened: true` — self-monitoring drives that exact
        // flag (see above), so reuse its sound rather than adding a dedicated one.
        void soundManager.play(SoundType.DEAFENED)
      } else {
        this.stopSelfMonitorPlayback()
        const publication = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)
        try {
          await publication?.resumeUpstream()
        } catch (error) {
          console.error('[LiveKitManager] Failed to resume upstream after self-monitor', error)
          Sentry.captureException(error, { extra: { action: 'self_monitor_resume_upstream' } })
        }
        this.assertCurrent(session)
        const prevMicMuted = this.selfMonitorPrevMicMuted ?? false
        const prevDeafened = this.selfMonitorPrevDeafened ?? false
        this.selfMonitorPrevMicMuted = null
        this.selfMonitorPrevDeafened = null
        this.micAudio.applyDeafState(session, prevDeafened)
        this.state.isMicMuted = prevMicMuted
        this.state.isDeafened = prevDeafened
        this.state.isSelfMonitoring = false
        void soundManager.play(SoundType.NON_DEAFENED)
      }
    })
  }

  /**
   * Starts (or restarts) the local playback loop backing `setSelfMonitoring(true)`, given the
   * already-resolved shared `AudioContext` (`sharedAudioContext()`, resolved by the caller before
   * flipping `state.isSelfMonitoring` — see there for why). Routed through that context rather
   * than a plain `new Audio()` + `.play()` — it's the exact same context `Room` already plays
   * every remote participant's audio through (`createRoomOptions`'s `webAudioMix`), so it's
   * already running/unlocked for the whole call and already gets its sink switched to the
   * selected output device by LiveKit's own `switchActiveDevice('audiooutput', ...)` (see
   * `Room.switchActiveDevice` in the SDK: `webAudioMix` -> `audioContext.setSinkId`). A fresh,
   * unconnected `<audio>` element has neither property — `.play()` called after even one `await`
   * easily lands outside the browser's "recent user gesture" window and gets silently rejected,
   * which is exactly why the first version of this feature produced no sound at all.
   */
  private startSelfMonitorPlayback(session: CallSession, audioContext: AudioContext) {
    this.stopSelfMonitorPlayback()

    // Same per-frame resync strategy as `micLevelMeter.ts`: the outgoing `mediaStreamTrack` can
    // swap out from under us at any time (noise-suppression/echo-cancellation toggle, device
    // switch) while monitoring is active, and polling once a frame catches every case uniformly
    // instead of depending on a specific LiveKit event firing.
    const tick = () => {
      if (this.session !== session || !this.state.isSelfMonitoring) return
      const publication = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)
      const liveTrack = publication?.track
      // Deliberately NOT `liveTrack.sender?.track` — we've intentionally nulled that out via
      // `pauseUpstreamCompat()` for the whole duration of self-monitoring, so it would read `null`
      // the entire time. When RNNoise is active, `track.mediaStreamTrack` already resolves to
      // `processor.processedTrack`, which has `micVolume` baked in via `processor.setVolume()`
      // (see `syncNoiseSuppression`/`syncMicVolume`) — but when it's off, `syncMicVolume` applies
      // volume through a separate `MicGainChain` swapped directly onto the sender out-of-band
      // (`chain.outputTrack`, never visible through `track.mediaStreamTrack`), so that has to be
      // checked first to make the mic-volume slider actually audible while self-monitoring.
      const gainChain = session.getMicGainChain()
      const mediaStreamTrack = gainChain && gainChain.outputTrack.readyState !== 'ended'
        ? gainChain.outputTrack
        : liveTrack instanceof LocalAudioTrack ? liveTrack.mediaStreamTrack : undefined
      if (mediaStreamTrack && mediaStreamTrack.id !== this.selfMonitorTrackId) {
        this.selfMonitorTrackId = mediaStreamTrack.id
        try { this.selfMonitorSourceNode?.disconnect() } catch { /* already disconnected */ }
        this.selfMonitorSourceNode = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]))
        this.selfMonitorSourceNode.connect(audioContext.destination)
      }
      this.selfMonitorRafId = requestAnimationFrame(tick)
    }
    tick()
  }

  private stopSelfMonitorPlayback() {
    if (this.selfMonitorRafId !== null) {
      cancelAnimationFrame(this.selfMonitorRafId)
      this.selfMonitorRafId = null
    }
    if (this.selfMonitorSourceNode) {
      try { this.selfMonitorSourceNode.disconnect() } catch { /* already disconnected */ }
      this.selfMonitorSourceNode = null
    }
    this.selfMonitorTrackId = null
  }

  async setVideoQuality(quality: VideoQuality) {
    this.state.videoQuality = quality
    patchAppSettings({ video: { videoQuality: quality } })
    const track = this.session?.room.localParticipant.getTrackPublication(Track.Source.Camera)?.track
    if (track instanceof LocalVideoTrack) await track.restartTrack({ resolution: cameraPreset(quality).resolution })
  }

  async setScreenShareQuality(quality: ScreenShareQuality) {
    this.state.screenShareQuality = quality
    patchAppSettings({ video: { screenShareQuality: quality } })
    const track = this.session?.room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track
    if (!(track instanceof LocalVideoTrack)) return
    const profile = effectiveScreenProfile(quality, this.state.screenShareFps)
    this.state.screenShareFps = profile.maxFramerate
    await reconfigureScreenShareTrack(track, profile)
  }

  async setScreenShareFps(fps: number) {
    const normalized = [5, 15, 30].includes(fps) ? fps : 30
    const profile = effectiveScreenProfile(this.state.screenShareQuality, normalized)
    this.state.screenShareFps = profile.maxFramerate
    const track = this.session?.room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track
    if (!(track instanceof LocalVideoTrack)) return
    await reconfigureScreenShareTrack(track, profile)
  }

  async setScreenSharePreset(preset: 'text' | 'motion') {
    const quality: ScreenShareQuality = preset === 'text' ? '1440p' : '720p'
    const fps = preset === 'text' ? 15 : 30
    this.state.screenShareQuality = quality
    this.state.screenShareFps = fps
    patchAppSettings({ video: { screenShareQuality: quality } })

    // Let the selected button render before MediaStreamTrack reconfiguration,
    // which can briefly occupy Chromium's media thread on high resolutions.
    await this.waitForUiFrame()
    const track = this.session?.room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track
    if (!(track instanceof LocalVideoTrack)) return
    const profile = effectiveScreenProfile(quality, fps)
    await reconfigureScreenShareTrack(track, profile)
  }

  async setScreenShareAudioEnabled(enabled: boolean) {
    this.state.isScreenShareAudioEnabled = enabled
    patchAppSettings({ video: { screenShareAudioEnabled: enabled } })
    const track = this.session?.room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio)?.track
    if (!(track instanceof LocalAudioTrack)) return
    if (enabled) await track.unmute()
    else await track.mute()
  }

  async refreshDevices(requestPermissions: boolean | { mic: boolean; camera: boolean } = true) {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return
    const { mic: requestMic, camera: requestCamera } = typeof requestPermissions === 'boolean'
      ? { mic: requestPermissions, camera: requestPermissions }
      : requestPermissions
    try {
      const enumerated = await enumerateDevices(requestMic, requestCamera)
      const { cameras } = enumerated
      // macOS: the pickers list what CoreAudio has (by name), not WebKit's ids — WebKit shows no
      // devices at all without a `getUserMedia` grant, which the native mic never makes.
      const microphones = this.useNativeMic ? await listNativeMicrophones() : enumerated.microphones
      const speakers = this.useNativeMic ? await listNativeSpeakers() : enumerated.speakers

      // A real, labeled result is worth remembering for next time this session's own
      // permission/label unlock hasn't happened yet (see `initializeDevices`) — e.g. the very
      // first launch ever, before any grant exists. Never cache a blank/placeholder list. This is
      // also the only signal that labels are actually unlocked *this session* — `devicechange`
      // keeps calling `refreshDevices(false)` afterwards and, per LiveKit's own `DeviceManager`,
      // keeps getting real labels back once this has fired once, so later calls never need to
      // flip it back off. Tracked separately per mic/camera — see `sessionMicLabelsConfirmed`.
      if (hasLabels(microphones)) this.sessionMicLabelsConfirmed = true
      if (hasLabels(cameras)) this.sessionCameraLabelsConfirmed = true
      if (hasLabels(microphones) || hasLabels(cameras) || hasLabels(speakers)) {
        saveDeviceListCache(microphones, cameras, speakers)
      }

      // If this particular kind's live list came back empty OR unlabeled (some WebViews return
      // nothing at all for audioinput/videoinput until permission is confirmed *this* session;
      // others return a single blank placeholder entry instead) fall back to the last known
      // labeled list purely for display — it's never used to resolve which device is actually
      // live (see `resolveDevicePreference`), only so pickers show real names instead of a lone
      // "Микрофон"/"default" placeholder while waiting for this session's own label unlock.
      const micLabeled = hasLabels(microphones)
      const cameraLabeled = hasLabels(cameras)
      const speakerLabeled = hasLabels(speakers)
      const cache = (!micLabeled || !cameraLabeled || !speakerLabeled) ? loadDeviceListCache() : null
      const withCacheFallback = (live: MediaDeviceInfo[], labeled: boolean, cached: CachedDeviceInfo[] | undefined) =>
        labeled || !cached?.length ? live : (cached as unknown as MediaDeviceInfo[])
      this.state.microphones = withCacheFallback(microphones, micLabeled, cache?.microphones)
      this.state.cameras = withCacheFallback(cameras, cameraLabeled, cache?.cameras)
      this.state.speakers = withCacheFallback(speakers, speakerLabeled, cache?.speakers)

      // Runs after every enumeration — startup, `devicechange`, opening the devices tab, or an
      // explicit permission grant — so a previously-picked device is re-applied the moment it's
      // visible again, without this call itself ever touching the mic/camera.
      await this.restoreDevicePreferences()
    } catch (error) {
      console.warn('[LiveKitManager] Device enumeration failed', error)
      Sentry.captureException(error, { level: 'warning', extra: { action: 'enumerate_devices' } })
    }
  }

  /**
   * Discord-style "quiet" label unlock: if this session hasn't yet gotten a real labeled mic or
   * camera list (see `sessionMicLabelsConfirmed`/`sessionCameraLabelsConfirmed`), and there's
   * actual evidence the OS/browser already granted that *specific* device before (a cached
   * labeled list from a past session, or a live `Permissions.query` reading of "granted"),
   * re-run enumeration for just that kind with `requestPermissions: true`. When the grant is
   * still valid this never shows a prompt or opens a visible/audible capture — the browser just
   * returns the real labels immediately, same as a plain `enumerateDevices()` would once
   * unlocked. Mic and camera are evaluated independently on purpose: evidence the mic was granted
   * before says nothing about the camera, and requesting the camera's labels just because the mic
   * happens to be unlocked would silently open the camera and light its hardware indicator for no
   * reason the user asked for. If a grant turned out to have been revoked meanwhile, this
   * fails/returns unlabeled exactly like any other `refreshDevices(true)` call and simply leaves
   * the cached/placeholder list in place — it never loops or nags the user.
   *
   * Neither mic nor camera is quietly unlocked at startup — for either, a bare `Permissions.query`
   * "granted" reading or a cached labeled list are permanent/durable signals (a device-list cache
   * entry, once labeled, never expires — see `saveDeviceListCache`) while what actually determines
   * whether `refreshDevices({ mic/camera: true })` stays silent is *this reload's*
   * `enumerateDevices()` result — which this app's WebView (notably on mobile) does not reliably
   * return labeled even after a real prior grant. Trusting either signal here means LiveKit's
   * `DeviceManager.getDevices()` falls back to a real `getUserMedia(...)` — which visibly opens the
   * mic/camera and lights its hardware indicator, and on some platforms re-shows the OS permission
   * prompt outright — on every single reload for any user who has ever granted access once. Device
   * labels are instead unlocked lazily, on an actual user gesture (opening the device picker,
   * joining a call, enabling the camera), where a momentary indicator flash/prompt is expected and
   * attributable to that action.
   */
  async ensureDeviceLabelsUnlocked() {
    // Intentionally a no-op — see doc comment above. Kept as a named call site (rather than
    // deleted) so `initializeDevices()` reads as "labels are unlocked lazily, not here".
  }

  private createRoomOptions(audioContext: AudioContext | null, audioBitrateKbps: number | undefined): RoomOptions {
    return {
      videoCaptureDefaults: { resolution: cameraPreset(this.state.videoQuality).resolution },
      audioCaptureDefaults: this.micAudio.microphoneCaptureOptions(this.state),
      publishDefaults: {
        simulcast: true,
        videoCodec: 'vp9',
        backupCodec: true,
        videoEncoding: cameraPreset(this.state.videoQuality).encoding,
        audioPreset: audioPresetForChannel(audioBitrateKbps),
        dtx: true,
        red: true,
        // Mute/unmute should just pause/resume sending audio, not tear the
        // capture down: with `true` LiveKit stops the MediaStreamTrack on
        // mute and re-runs getUserMedia on unmute, which is the actual
        // source of the toggle-mic lag — not anything server-side.
        stopMicTrackOnMute: false,
      },
      adaptiveStream: true,
      dynacast: true,
      // Per-participant volume goes up to 200%, and a plain `<audio>.volume` throws
      // above 1.0 — routing remote audio through a Web Audio GainNode is what lets
      // `RemoteAudioTrack.setVolume()` boost past unity instead of clamping/erroring.
      // Passing our own already-primed context (see `installEarlyAudioContextPrime`/
      // `MicAudioController.prime`) matters, not just cosmetic: with a bare `true`, LiveKit's own
      // `Room.acquireAudioContext()` creates a *second*, brand-new `AudioContext` right inside
      // `room.connect()` regardless of any priming we did — that's the actual first open of the
      // OS audio engine, which is what was producing the glitch, not our own separate context.
      // Reusing ours means the OS-level open already happened earlier, decoupled from the join.
      webAudioMix: audioContext ? { audioContext } : true,
    }
  }

  // `RoomEvent.ActiveSpeakersChanged` only reports an aggregate, polled snapshot of who's
  // currently active — a per-participant `ParticipantEvent.IsSpeakingChanged` listener fires
  // immediately on each speaking-state flip, so the tile border reacts without that delay.
  // Must be called for the local participant, every remote participant already in the room
  // at connect time, and every remote participant that joins afterward (see call sites).
  private attachSpeakingListener(session: CallSession, participant: Participant) {
    session.listenParticipant(participant, ParticipantEvent.IsSpeakingChanged, () => this.updateParticipants(session))
  }

  private installRoomListeners(session: CallSession) {
    const update = () => this.updateParticipants(session)
    session.listen(RoomEvent.ParticipantConnected, (participant: Participant) => {
      this.attachSpeakingListener(session, participant)
      update()
      void soundManager.play(SoundType.CONNECT)
    })
    session.listen(RoomEvent.ParticipantDisconnected, (participant: Participant) => {
      session.unlistenParticipant(participant)
      delete this.state.watchedScreenShares[participant.identity]
      update()
      void soundManager.play(SoundType.DISCONNECT)
    })
    session.listen(RoomEvent.TrackPublished, (publication: any, participant: any) => {
      // A new screen share isn't watched until asked for (`setScreenShareWatched`) — turn off the
      // automatic subscription right as it's announced.
      if (this.isUnwatchedScreenShare(publication, participant)) publication.setSubscribed(false)
      update()
      if (publication.source === Track.Source.ScreenShare) void soundManager.play(SoundType.SCREEN_SHARE_START)
    })
    session.listen(RoomEvent.TrackUnpublished, (publication: any, participant: any) => {
      // Their next screen share starts unwatched again, like a new stream in Discord.
      if (publication.source === Track.Source.ScreenShare && participant?.identity) {
        delete this.state.watchedScreenShares[participant.identity]
      }
      update()
      if (publication.source === Track.Source.ScreenShare) void soundManager.play(SoundType.SCREEN_SHARE_STOP)
    })
    session.listen(RoomEvent.LocalTrackUnpublished, (publication: any) => {
      update()
      if (publication.source === Track.Source.ScreenShare) void soundManager.play(SoundType.SCREEN_SHARE_STOP)
    })
    session.listen(RoomEvent.TrackMuted, update)
    session.listen(RoomEvent.TrackUnmuted, update)
    session.listen(RoomEvent.ParticipantMetadataChanged, update)
    session.listen(RoomEvent.ParticipantAttributesChanged, update)
    session.listen(RoomEvent.ActiveSpeakersChanged, update)
    session.listen(RoomEvent.ConnectionQualityChanged, update)
    session.listen(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      if (this.session === session) this.state.roomState = state
    })
    session.listen(RoomEvent.Reconnecting, () => {
      if (this.session !== session) return
      this.state.isReconnecting = true
      void soundManager.startLoop(SoundType.RECONNECTING)
    })
    session.listen(RoomEvent.Reconnected, () => {
      if (this.session !== session) return
      this.state.isReconnecting = false
      void soundManager.stopLoop()
    })
    session.listen(RoomEvent.LocalTrackPublished, (publication: any) => {
      update()
      if (publication.track?.source === Track.Source.Microphone) {
        session.setTimer(() => {
          void this.queue.run(async () => {
            if (this.session !== session) return
            await this.syncNoiseSuppression(session)
            await this.syncMicVolume(session)
          })
        }, 250)
      }
      if (publication.source === Track.Source.ScreenShare) void soundManager.play(SoundType.SCREEN_SHARE_START)
    })
    session.listen(RoomEvent.TrackSubscribed, (track: any, publication: any, participant: any) => {
      // Screen shares that were already live when we joined never fire `TrackPublished` and get
      // auto-subscribed — undo that here unless they're being watched.
      if (this.isUnwatchedScreenShare(publication, participant)) {
        publication.setSubscribed(false)
        return
      }
      if (track.kind !== Track.Kind.Audio) {
        // Camera tracks a participant already had published before we joined never fire
        // RoomEvent.TrackPublished (LiveKit only emits that for tracks published after both
        // sides are connected), so this is the only signal that their video just became usable —
        // without it, isVideoEnabled stays stale/false for participants who were already in the call.
        if (track.kind === Track.Kind.Video) update()
        return
      }
      // "Mute for me" (see `setParticipantMuted`) persists across reconnects, but LiveKit
      // auto-subscribes every published track by default — so a locally-muted participant's mic
      // still gets auto-subscribed on every fresh join/reconnect before we get a say. Undo that
      // immediately here instead of attaching/hearing it even briefly.
      if (track.source === Track.Source.Microphone && participant?.identity && this.state.mutedParticipants[participant.identity]) {
        publication.setSubscribed(false)
        return
      }
      track.setVolume?.(this.effectiveParticipantVolume(participant?.identity, track.source))
      track.attach()
      if (this.state.isDeafened) publication.setEnabled(false)
    })
    session.listen(RoomEvent.Disconnected, () => {
      if (session.closed || this.session !== session) return
      void this.queue.run(async () => {
        if (session.closed || this.session !== session) return
        await this.disconnectSession(session, true)
      })
    })
  }

  private async stopScreenShare(session: CallSession) {
    await disableScreenShare(session)
    if (this.session !== session) return
    this.state.isScreenShareEnabled = false
    this.updateParticipants(session)
  }

  private async disconnectInternal(playSound: boolean) {
    const session = this.session
    if (session) await this.disconnectSession(session, playSound)
    else this.resetCallState()
  }

  private async disconnectSession(session: CallSession, playSound: boolean) {
    await session.dispose()
    if (this.session !== session) return
    this.session = null
    this.resetCallState()
    if (playSound) await soundManager.play(SoundType.DISCONNECT)
  }

  /** macOS: (re)opens native playback on the selected output. On failure, WebKit keeps playing to the system default. */
  private async startNativeSpeaker() {
    try {
      await (await this.micAudio.nativeSpeaker())?.start(this.state.selectedSpeakerId)
    } catch (error) {
      console.error('[LiveKitManager] Native audio output failed to start', error)
      Sentry.captureException(error, { extra: { action: 'native-speaker-start' } })
    }
  }

  private resetCallState() {
    // Covers every disconnect path (normal, error rollback, unexpected drop, or LiveKit
    // giving up on reconnecting outright) so the loop can never outlive the call.
    if (this.state.isReconnecting) void soundManager.stopLoop()
    setCallAudioSessionActive(false)
    this.micAudio.stopNativeSpeaker()
    // A dropped call ends self-monitoring too — no session left to resume upstream on, so just
    // tear down local playback and restore whatever mic/deafen state it had overridden, the same
    // values `setSelfMonitoring(false)` would have restored.
    if (this.state.isSelfMonitoring) {
      this.state.isMicMuted = this.selfMonitorPrevMicMuted ?? this.state.isMicMuted
      this.state.isDeafened = this.selfMonitorPrevDeafened ?? this.state.isDeafened
    }
    this.stopSelfMonitorPlayback()
    this.state.isSelfMonitoring = false
    this.selfMonitorPrevMicMuted = null
    this.selfMonitorPrevDeafened = null
    this.state.room = null
    this.state.roomState = ConnectionState.Disconnected
    this.state.isReconnecting = false
    this.state.activeVoiceChannelId = null
    this.state.participants = []
    this.state.watchedScreenShares = {}
    // `isMicMuted`/`isDeafened` are deliberately NOT reset here — they're standing preferences
    // (see their declarations above), not per-call transients like camera/screen-share.
    this.state.isCameraEnabled = false
    this.state.isScreenShareEnabled = false
    this.state.isNoiseSuppressionPending = false
  }

  private updateParticipants(session: CallSession) {
    if (this.session !== session || session.closed) return
    const participants = [session.room.localParticipant, ...session.room.remoteParticipants.values()]
    this.state.participants = participants.map(participant => ({
      sid: participant.sid,
      identity: participant.identity,
      name: participant.name || participant.identity,
      isLocal: participant instanceof LocalParticipant,
      joinedAt: (participant as any).joinedAt?.getTime?.() ?? Date.now(),
      isSpeaking: participant.isSpeaking,
      audioLevel: participant.audioLevel,
      attributes: participant.attributes,
      isVideoEnabled: participant.isCameraEnabled,
      // For the local participant, `isMicrophoneEnabled` only flips true once the mic track is
      // actually captured and published — there's a real await gap between `roomState` becoming
      // `Connected` and `setMicrophoneEnabled()` resolving (see `connect()`), during which several
      // other listeners (`ConnectionQualityChanged`, `ParticipantAttributesChanged`, ...) already
      // trigger `updateParticipants()` and would otherwise render a spurious "muted" flash even
      // though the user never muted anything. `state.isMicMuted` is the standing intent/preference
      // and is kept in sync with the real state everywhere it changes, so it reads correctly even
      // before the track publish confirms.
      isAudioEnabled: participant instanceof LocalParticipant ? !this.state.isMicMuted : participant.isMicrophoneEnabled,
      isScreenShareEnabled: participant.isScreenShareEnabled,
      isHandRaised: participant.metadata?.includes('handRaised') ?? false,
      connectionQuality: participant.connectionQuality,
    }))
  }

  private async restartMicrophoneIfActive() {
    const session = this.session
    if (!session || this.state.isMicMuted) return
    // Native capture has no capture constraints to re-apply (no echo cancellation there).
    if (this.useNativeMic) return
    // Deliberately NOT disposing the mic gain chain here before the restart: `chain.outputTrack`
    // is whatever's currently live on `track.sender` (swapped in directly, out-of-band from
    // LiveKit's own `_mediaStreamTrack`/processor bookkeeping — see `MicGainChain`), so stopping
    // it here would kill the actually-transmitting track for the entire span of `restartTrack()`
    // instead of just its own brief internal swap window. `syncMicVolume()` below already disposes
    // and rebuilds the chain against whatever track the restart lands on, so there's no need to
    // pre-emptively tear it down.
    const track = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
    const captureOptions = this.micAudio.microphoneCaptureOptions(this.state)
    try {
      if (track instanceof LocalAudioTrack) await track.restartTrack(captureOptions)
      else await this.setMicrophoneEnabled(session, true)
    } catch (error) {
      // A failed `restartTrack()` (e.g. `getUserMedia` transiently rejecting the new
      // constraints) can leave the sender pointed at whatever track it had mid-swap, with no
      // further sync ever re-running — previously the only recovery was a full channel
      // reconnect. Falling back to a full `setMicrophoneEnabled` re-publish gives the track a
      // second, independent chance to (re)acquire a live device track before giving up.
      console.error('[LiveKitManager] Mic restart failed, retrying with a full re-publish', error)
      Sentry.captureException(error, { extra: { action: 'restart_microphone' } })
      this.assertCurrent(session)
      await this.setMicrophoneEnabled(session, true)
    }
    this.assertCurrent(session)
    await this.syncNoiseSuppression(session)
    await this.syncMicVolume(session)
  }

  /**
   * Every mic on/off in a call goes through here. Off is always LiveKit's own mute (the capture
   * keeps running, see `stopMicTrackOnMute`). On, on macOS, publishes the native capture's track
   * the first time (`publishNativeMicrophone`) and then just unmutes it — LiveKit never re-acquires
   * a user-provided track, so `getUserMedia` is never reached there.
   */
  private async setMicrophoneEnabled(session: CallSession, enabled: boolean) {
    const localParticipant = session.room.localParticipant
    const publishOptions = {
      audioPreset: audioPresetForChannel(session.audioBitrateKbps),
      dtx: true,
      red: true,
    }
    if (this.useNativeMic && enabled && !localParticipant.getTrackPublication(Track.Source.Microphone)) {
      await this.publishNativeMicrophone(session, publishOptions)
      return
    }
    await localParticipant.setMicrophoneEnabled(enabled, this.micAudio.microphoneCaptureOptions(this.state), publishOptions)
  }

  private async publishNativeMicrophone(session: CallSession, publishOptions: { audioPreset: { maxBitrate: number }; dtx: boolean; red: boolean }) {
    const audioContext = await this.micAudio.sharedAudioContext()
    this.assertCurrent(session)
    if (!audioContext) throw new Error('Не удалось получить аудиоконтекст для микрофона')
    const source = await NativeMicSource.start(audioContext, this.state.selectedMicId)
    // Owned by the session before the next await; stopped right away if the call already ended.
    session.adoptNativeMic(source)
    this.assertCurrent(session)
    await session.room.localParticipant.publishTrack(source.track, {
      ...publishOptions,
      source: Track.Source.Microphone,
    })
  }

  private async syncNoiseSuppression(session: CallSession) {
    if (this.session !== session || session.closed) return
    this.state.isNoiseSuppressionPending = true
    try {
      await this.micAudio.syncNoiseSuppression(session, this.state)
    } finally {
      if (this.session === session) this.state.isNoiseSuppressionPending = false
    }
    await this.reassertSelfMonitorPause(session)
  }

  private async syncMicVolume(session: CallSession) {
    if (this.session !== session || session.closed) return
    await this.micAudio.syncMicVolume(session, this.state.micVolume)
    await this.reassertSelfMonitorPause(session)
  }

  /**
   * `LocalAudioTrack.setProcessor()`/`internalStopProcessor()` (driven by `syncNoiseSuppression`)
   * and the manual `MicGainChain` swap (driven by `syncMicVolume`) both unconditionally call
   * `sender.replaceTrack(...)` to apply their change — neither checks `track.isUpstreamPaused`
   * first, so adjusting the mic-volume slider or toggling noise suppression while
   * `setSelfMonitoring(true)` has us paused (see there) would silently resume actually
   * transmitting to other participants. Re-pausing here after every such sync closes that gap
   * without needing every future sync path to remember to check for it itself.
   */
  private async reassertSelfMonitorPause(session: CallSession) {
    if (!this.state.isSelfMonitoring || this.session !== session || session.closed) return
    const publication = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)
    const track = publication?.track
    if (!publication || !(track instanceof LocalAudioTrack) || !track.sender?.track) return
    try {
      await pauseUpstreamCompat(publication, track)
    } catch (error) {
      console.error('[LiveKitManager] Failed to re-pause upstream for self-monitor', error)
      Sentry.captureException(error, { extra: { action: 'self_monitor_reassert_pause' } })
    }
  }

  private saveMediaDevice(key: MediaDeviceKey, deviceId: string) {
    this.state[key] = deviceId
    patchAppSettings({ media: { [key]: deviceId } })

    // Only a real, currently-known device is worth remembering as a restorable preference.
    // «Как в системе» (`deviceId === 'default'`) clears it instead — keeping the previous one let
    // the next enumeration («Обновить», `devicechange`) restore that device over the user's choice.
    const device = deviceId === 'default'
      ? undefined
      : selectableDevices(this.deviceListForKey(key)).find(d => d.deviceId === deviceId)
    if (device) saveDevicePreference(MEDIA_KEY_SLOT[key], device)
    else if (deviceId === 'default') clearDevicePreference(MEDIA_KEY_SLOT[key])
  }

  private deviceListForKey(key: MediaDeviceKey): MediaDeviceInfo[] {
    if (key === 'selectedMicId') return this.state.microphones
    if (key === 'selectedCameraId') return this.state.cameras
    return this.state.speakers
  }

  /**
   * Re-applies any previously-picked mic/camera/speaker that's visible again in the current
   * device list. Never calls `getUserMedia`/requests permissions itself — it only ever reads
   * `state.microphones/cameras/speakers` (already populated by the caller, `refreshDevices`) and,
   * if a match is found, goes through the same `changeMicrophone`/`changeCamera`/`changeSpeaker`
   * used for a manual device switch. Those already no-op the hardware side when there's no active
   * call (`session` is null) — see each method — so outside a call this only ever updates state.
   */
  private async restoreDevicePreferences() {
    await Promise.all([
      this.restoreDeviceSlot('selectedMicId'),
      this.restoreDeviceSlot('selectedCameraId'),
      this.restoreDeviceSlot('selectedSpeakerId'),
    ])
  }

  private async restoreDeviceSlot(key: MediaDeviceKey) {
    const slot = MEDIA_KEY_SLOT[key]
    const preference = getDevicePreference(slot)
    if (!preference) return

    const match = resolveDevicePreference(preference, selectableDevices(this.deviceListForKey(key)))
    if (!match || match.deviceId === this.state[key]) return

    // Never touch an already-live call automatically. This used to self-heal back to the exact
    // original device reappearing mid-call (e.g. a reconnecting Bluetooth headset), but that
    // switch is a real `switchActiveDevice`/`setSinkId` call, not a free state update — on the
    // very first join of a session it fired the instant the join's own `getUserMedia`/permission
    // unlock made labels visible for the first time (see `initializeDevices`), producing an
    // audible ~1s glitch on the user's system audio output right as they connected. Restoring a
    // preference must never be able to cause an audible interruption on its own — if the
    // preferred device comes back while a call is already running, let the user reconnect it
    // manually (settings page / device picker) instead.
    if (this.session) return

    try {
      if (key === 'selectedMicId') await this.changeMicrophone(match.deviceId)
      else if (key === 'selectedCameraId') await this.changeCamera(match.deviceId)
      else await this.changeSpeaker(match.deviceId)
    } catch (error) {
      console.warn(`[LiveKitManager] Failed to restore ${slot} preference`, error)
      Sentry.captureException(error, { level: 'warning', extra: { action: 'restore_device_preference', slot } })
    }
  }

  private waitForUiFrame() {
    if (typeof requestAnimationFrame === 'undefined') return Promise.resolve()
    return new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
  }

  private assertCurrent(session: CallSession) {
    if (this.session !== session || session.closed) throw new DOMException('Сессия звонка завершена', 'AbortError')
  }
}

export const liveKitManager = new LiveKitManager()
